import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, MapPinIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useGoogleMapsApi, FormattedAddress } from '../../hooks/useGoogleMapsApi';

export interface GoogleAddressInputProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect?: (address: FormattedAddress) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  helpText?: string;
  error?: string;
  className?: string;
  addressType?: 'business' | 'personal';
}

export default function GoogleAddressInput({
  value,
  onChange,
  onAddressSelect,
  placeholder,
  disabled = false,
  required = false,
  label,
  helpText,
  error,
  className = '',
  addressType = 'personal'
}: GoogleAddressInputProps) {
  const { t, i18n } = useTranslation();
  const { isLoaded, searchPlaces, getPlaceDetails } = useGoogleMapsApi();
  
  const [predictions, setPredictions] = useState<google.maps.places.PlacePrediction[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<FormattedAddress | null>(null);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search for places with debouncing
  const searchForPlaces = async (query: string) => {
    console.log('searchForPlaces called:', { query, queryLength: query.trim().length, isLoaded });
    
    if (!isLoaded || !query.trim() || query.trim().length < 3) {
      console.log('Search cancelled:', { isLoaded, queryLength: query.trim().length });
      setPredictions([]);
      return;
    }

    console.log('Starting search...');
    setIsLoading(true);
    
    try {
      const options: {
        languageCode?: string;
        locationBias?: google.maps.places.LocationBias;
        locationRestriction?: google.maps.places.LocationRestriction;
      } = {};
      
      // Add language preference based on user's i18n setting
      // Convert i18n language code (e.g., 'en', 'he', 'ar') to Google's format (e.g., 'en-US', 'he-IL', 'ar-SA')
      const languageMap: Record<string, string> = {
        'en': 'en-US',
        'he': 'he-IL', 
        'ar': 'ar-SA',
        'fr': 'fr-FR',
        'de': 'de-DE'
      };
      const currentLanguage = languageMap[i18n.language] || 'en-US';
      options.languageCode = currentLanguage;
      
      const results = await searchPlaces(query, options);
      setPredictions(results);
      setIsOpen(results.length > 0);
    } catch (err) {
      console.error('Error searching places:', err);
      setPredictions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change with debouncing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    // Clear selected address when user starts typing again
    if (selectedAddress) {
      setSelectedAddress(null);
    }

    // Debounce the search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      searchForPlaces(newValue);
    }, 300);
  };

  // Handle prediction selection
  const handlePredictionSelect = async (prediction: google.maps.places.PlacePrediction) => {
    setIsLoading(true);
    setIsOpen(false);
    
    try {
      // Update input value immediately - construct description from new API fields
      const mainText = prediction.mainText?.text || '';
      const secondaryText = prediction.secondaryText?.text || '';
      const description = secondaryText ? `${mainText}, ${secondaryText}` : mainText;
      onChange(description);
      
      // Get detailed address information
      const addressDetails = await getPlaceDetails(prediction.placeId);
      
      if (addressDetails) {
        setSelectedAddress(addressDetails);
        onAddressSelect?.(addressDetails);
      }
    } catch (err) {
      console.error('Error getting place details:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const getPlaceholderText = () => {
    if (placeholder) return placeholder;
    
    if (addressType === 'business') {
      return t('address.businessPlaceholder', 'Enter business address (Street, City, State, Postal Code, Country)');
    }
    return t('address.personalPlaceholder', 'Enter home address (Street, City, State, Postal Code, Country)');
  };

  const getHelpText = () => {
    if (helpText) return helpText;
    
    if (addressType === 'business') {
      return t('address.businessHelp', 'Provide the registered business address or primary location where legal documents can be served.');
    }
    return t('address.personalHelp', 'Provide the residential address or known address where legal documents can be served.');
  };

  return (
    <div className={`relative ${className}`}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Input Container */}
      <div className="relative">
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (predictions.length > 0) {
                setIsOpen(true);
              }
            }}
            placeholder={getPlaceholderText()}
            disabled={disabled || !isLoaded}
            className={`
              w-full px-3 py-2 pr-10 border rounded-md transition-colors duration-200
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
              disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
              hover:border-gray-400 disabled:hover:border-gray-300
              ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}
              ${selectedAddress ? 'bg-green-50 border-green-300' : ''}
            `}
          />
          
          {/* Loading/Success/Dropdown Icon */}
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {isLoading ? (
              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
            ) : selectedAddress ? (
              <CheckIcon className="h-4 w-4 text-green-500" />
            ) : predictions.length > 0 ? (
              <ChevronDownIcon 
                className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
              />
            ) : (
              <MapPinIcon className="h-4 w-4 text-gray-400" />
            )}
          </div>
        </div>

        {/* Predictions Dropdown */}
        {isOpen && predictions.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
          >
            {predictions.map((prediction) => (
              <button
                key={prediction.placeId}
                type="button"
                onClick={() => handlePredictionSelect(prediction)}
                className="w-full px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0 transition-colors duration-150"
              >
                <div className="flex items-start space-x-2">
                  <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {prediction.mainText?.text || 'Unknown location'}
                    </div>
                    {prediction.secondaryText?.text && (
                      <div className="text-xs text-gray-500">
                        {prediction.secondaryText.text}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
            
            {/* Powered by Google */}
            <div className="px-3 py-2 border-t border-gray-200 bg-gray-50">
              <img
                src="https://developers.google.com/maps/documentation/images/powered_by_google_on_white.png"
                alt="Powered by Google"
                className="h-3"
              />
            </div>
          </div>
        )}
      </div>

      {/* Help Text */}
      {!error && (
        <p className="mt-1 text-xs text-gray-500">
          {getHelpText()}
        </p>
      )}

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-xs text-red-600">
          {error}
        </p>
      )}

      {/* API Not Loaded Warning */}
      {!isLoaded && (
        <p className="mt-1 text-xs text-yellow-600">
          {t('address.apiNotLoaded', 'Google Maps API is loading...')}
        </p>
      )}
    </div>
  );
}